import type { FormEvent } from "react";
import { useEffect, useMemo, useState } from "react";
import type {
  AdminLessonDetailDto,
  AdminMembersResponseDto,
  LessonStatus,
  MemberRole,
  StudioDecisionStatus,
  TeacherAssignmentStatus,
  StudiosResponseDto
} from "@madamuh/domain";
import { getRecurrencePresetLabel } from "@madamuh/domain";
import { Link, useNavigate, useParams } from "react-router-dom";
import { fetchAdminMembers, fetchAdminStudios } from "../../shared/api/adminDirectory";
import {
  cancelAdminManualOccurrence,
  createAdminManualOccurrence,
  fetchAdminLesson,
  patchAdminLesson,
  transitionAdminLesson
} from "../../shared/api/adminLesson";
import { formatTenantIsoDate, formatTenantIsoTime } from "../../shared/lib/tenantDateTime";
import {
  getProtectedApiErrorMessage,
  isUnauthenticatedApiError
} from "../../shared/session/protectedApiError";
import { useProtectedRouteSession } from "../../shared/session/useProtectedRouteSession";
import { OperatorPageScaffold } from "../../shared/ui/protected/OperatorPageScaffold";

const adminLinks = [
  { to: "/t/:tenantSlug/teacher/admin", label: "管理ホーム" },
  { to: "/t/:tenantSlug/teacher/admin/lessons", label: "レッスン一覧" }
];

const WEEKDAY_LABELS = ["日", "月", "火", "水", "木", "金", "土"] as const;

type LessonEditState = {
  title: string;
  shortTitle: string;
  description: string;
  genre: string;
  targetLabel: string;
  publicVisible: boolean;
  trialPolicyId: string;
  visibilityPolicyId: string;
  sortOrder: string;
};

type ManualOccurrenceFormState = {
  occurrenceDate: string;
  startTime: string;
  endTime: string;
  studioId: string;
  studioDecisionStatus: StudioDecisionStatus;
  teacherPlatformUserIds: string[];
  teacherAssignmentStatus: TeacherAssignmentStatus;
  publicNote: string;
  operatorNote: string;
  adminNote: string;
};

function toEditState(lesson: AdminLessonDetailDto): LessonEditState {
  return {
    title: lesson.title,
    shortTitle: lesson.shortTitle ?? "",
    description: lesson.description ?? "",
    genre: lesson.genre ?? "",
    targetLabel: lesson.targetLabel ?? "",
    publicVisible: lesson.publicVisible,
    trialPolicyId: lesson.trialPolicyId ?? "",
    visibilityPolicyId: lesson.visibilityPolicyId ?? "",
    sortOrder: String(lesson.sortOrder)
  };
}

function createInitialManualOccurrenceFormState(lesson?: AdminLessonDetailDto | null): ManualOccurrenceFormState {
  const firstSlot = lesson?.recurringSlots[0] ?? null;
  const defaultStudioDecisionStatus: StudioDecisionStatus =
    lesson?.defaultStudioDecisionStatus === "confirmed" || lesson?.defaultStudioDecisionStatus === "tbd"
      ? lesson.defaultStudioDecisionStatus
      : firstSlot?.studioDecisionStatus === "confirmed" || firstSlot?.studioDecisionStatus === "tbd"
        ? firstSlot.studioDecisionStatus
        : "tbd";
  const defaultTeacherAssignmentStatus: TeacherAssignmentStatus =
    lesson?.defaultTeacherAssignmentStatus === "assigned" || lesson?.defaultTeacherAssignmentStatus === "tbd"
      ? lesson.defaultTeacherAssignmentStatus
      : firstSlot?.teacherAssignmentStatus === "assigned" || firstSlot?.teacherAssignmentStatus === "tbd"
        ? firstSlot.teacherAssignmentStatus
        : "tbd";

  return {
    occurrenceDate: firstSlot?.activeFrom ?? lesson?.startDate ?? "",
    startTime: lesson?.defaultStartTime ?? firstSlot?.startTime ?? "10:00",
    endTime: lesson?.defaultEndTime ?? firstSlot?.endTime ?? "11:30",
    studioId: lesson?.defaultStudioId ?? firstSlot?.studioId ?? "",
    studioDecisionStatus: defaultStudioDecisionStatus,
    teacherPlatformUserIds:
      lesson?.defaultTeacherPlatformUserIds.length
        ? lesson.defaultTeacherPlatformUserIds
        : (firstSlot?.defaultTeacherPlatformUserIds ?? []),
    teacherAssignmentStatus: defaultTeacherAssignmentStatus,
    publicNote: "",
    operatorNote: "",
    adminNote: ""
  };
}

function hasTeacherRole(roles: readonly MemberRole[]): boolean {
  return roles.includes("teacher") || roles.includes("admin");
}

function getMemberDisplayName(member: AdminMembersResponseDto["items"][number]): string {
  return member.appNickname ?? member.lineDisplayNameSnapshot ?? member.platformUserId;
}

function toggleSelection(current: readonly string[], value: string, checked: boolean): string[] {
  const next = new Set(current);

  if (checked) {
    next.add(value);
  } else {
    next.delete(value);
  }

  return [...next];
}

function getAvailableTransitions(status: LessonStatus): LessonStatus[] {
  switch (status) {
    case "draft":
      return ["active"];
    case "active":
      return ["paused", "ended"];
    case "paused":
      return ["active", "ended"];
    case "ended":
      return ["archived"];
    default:
      return [];
  }
}

export function AdminLessonDetailPage() {
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant", lessonId = "" } = useParams();
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const [lesson, setLesson] = useState<AdminLessonDetailDto | null>(null);
  const [members, setMembers] = useState<AdminMembersResponseDto | null>(null);
  const [studios, setStudios] = useState<StudiosResponseDto | null>(null);
  const [editState, setEditState] = useState<LessonEditState | null>(null);
  const [manualOccurrenceForm, setManualOccurrenceForm] = useState<ManualOccurrenceFormState>(
    createInitialManualOccurrenceFormState()
  );
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
  const [lastManualOccurrenceYearMonth, setLastManualOccurrenceYearMonth] = useState<string | null>(null);
  const hasAdminRole = Boolean(session?.member?.roles.includes("admin"));
  const transitions = useMemo(() => (lesson ? getAvailableTransitions(lesson.status) : []), [lesson]);
  const teacherMembers = useMemo(
    () => members?.items.filter((member) => hasTeacherRole(member.roles)) ?? [],
    [members]
  );

  async function refreshLesson() {
    setLoadState("loading");
    setLoadError(null);
    setFeedbackMessage(null);

    try {
      const [nextLesson, nextMembers, nextStudios] = await Promise.all([
        fetchAdminLesson(tenantSlug, lessonId),
        fetchAdminMembers(tenantSlug),
        fetchAdminStudios(tenantSlug)
      ]);
      setLesson(nextLesson);
      setMembers(nextMembers);
      setStudios(nextStudios);
      setEditState(toEditState(nextLesson));
      setManualOccurrenceForm((current) => current.occurrenceDate ? current : createInitialManualOccurrenceFormState(nextLesson));
      setLoadState("idle");
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadState("error");
      setLoadError(getProtectedApiErrorMessage(error, "lesson 詳細の取得に失敗しました"));
    }
  }

  useEffect(() => {
    if (!isSessionReady || !hasAdminRole) {
      return;
    }

    void refreshLesson();
  }, [hasAdminRole, isSessionReady, lessonId, tenantSlug]);

  async function handleSave(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!editState) {
      return;
    }

    setBusyKey("save");
    setLoadError(null);
    setFeedbackMessage(null);

    try {
      await patchAdminLesson(tenantSlug, lessonId, {
        title: editState.title.trim(),
        shortTitle: editState.shortTitle.trim() || null,
        description: editState.description.trim() || null,
        genre: editState.genre.trim() || null,
        targetLabel: editState.targetLabel.trim() || null,
        publicVisible: editState.publicVisible,
        trialPolicyId: editState.trialPolicyId.trim() || null,
        visibilityPolicyId: editState.visibilityPolicyId.trim() || null,
        sortOrder: Number.parseInt(editState.sortOrder, 10) || 0
      });
      await refreshLesson();
      setFeedbackMessage("lesson を更新しました。");
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, "lesson 更新に失敗しました"));
    } finally {
      setBusyKey(null);
    }
  }

  async function handleTransition(nextStatus: LessonStatus) {
    setBusyKey(`transition:${nextStatus}`);
    setLoadError(null);
    setFeedbackMessage(null);

    try {
      await transitionAdminLesson(tenantSlug, lessonId, { toStatus: nextStatus });
      await refreshLesson();
      setFeedbackMessage(`lesson status を ${nextStatus} に変更しました。`);
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, "lesson status transition に失敗しました"));
    } finally {
      setBusyKey(null);
    }
  }

  async function handleCreateManualOccurrence(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!lesson) {
      return;
    }

    setBusyKey("create-manual-occurrence");
    setLoadError(null);
    setFeedbackMessage(null);
    setLastManualOccurrenceYearMonth(null);

    try {
      const result = await createAdminManualOccurrence(tenantSlug, lessonId, {
        occurrenceDate: manualOccurrenceForm.occurrenceDate,
        startTime: manualOccurrenceForm.startTime,
        endTime: manualOccurrenceForm.endTime,
        studioId:
          manualOccurrenceForm.studioDecisionStatus === "tbd" ? null : manualOccurrenceForm.studioId || null,
        studioDecisionStatus: manualOccurrenceForm.studioDecisionStatus,
        teacherPlatformUserIds:
          manualOccurrenceForm.teacherAssignmentStatus === "tbd" ? [] : manualOccurrenceForm.teacherPlatformUserIds,
        teacherAssignmentStatus: manualOccurrenceForm.teacherAssignmentStatus,
        publicNote: manualOccurrenceForm.publicNote.trim() || null,
        operatorNote: manualOccurrenceForm.operatorNote.trim() || null,
        adminNote: manualOccurrenceForm.adminNote.trim() || null
      });
      setManualOccurrenceForm((current) => ({
        ...createInitialManualOccurrenceFormState(lesson),
        occurrenceDate: current.occurrenceDate
      }));
      setLastManualOccurrenceYearMonth(result.affectedYearMonths[0] ?? null);
      await refreshLesson();
      setFeedbackMessage(
        lesson.lessonKind === "regular"
          ? `個別予定を追加しました。(${result.items.length}件)`
          : `manual occurrence を追加しました。(${result.items.length}件)`
      );
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, "manual occurrence 作成に失敗しました"));
    } finally {
      setBusyKey(null);
    }
  }

  async function handleCancelManualOccurrence(occurrenceId: string) {
    setBusyKey(`cancel:${occurrenceId}`);
    setLoadError(null);
    setFeedbackMessage(null);

    try {
      await cancelAdminManualOccurrence(tenantSlug, occurrenceId);
      await refreshLesson();
      setFeedbackMessage("manual occurrence を取消しました。");
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, "manual occurrence の取消に失敗しました"));
    } finally {
      setBusyKey(null);
    }
  }

  return (
    <OperatorPageScaffold
      currentArea="admin"
      tenantSlug={tenantSlug}
      tenantName={session?.tenant.displayName}
      session={session}
      title="Admin Lesson Detail"
      description="lesson の詳細、top-level 編集、status transition、個別予定追加を確認する review 画面です。"
      links={adminLinks}
    >
      <div style={{ display: "grid", gap: 16 }}>
        {routeStatus === "checking" || sessionStatus === "loading" ? <p>セッションを確認しています。</p> : null}
        {routeStatus === "redirecting" ? <p>Entry へ移動しています。</p> : null}
        {sessionStatus === "error" ? <p>セッション確認に失敗しました。{errorMessage}</p> : null}
        {routeStatus === "authenticated" && !hasAdminRole ? <p>この画面は admin role のみ利用できます。</p> : null}
        {loadState === "loading" ? <p>lesson 詳細を読み込んでいます。</p> : null}
        {loadError ? <p>{loadError}</p> : null}
        {feedbackMessage ? <p>{feedbackMessage}</p> : null}
        {lastManualOccurrenceYearMonth ? (
          <div style={{ display: "grid", gap: 4, fontSize: 14 }}>
            <Link to={`/t/${tenantSlug}/teacher/admin/schedule-months/${lastManualOccurrenceYearMonth}`}>
              影響したスケジュール月 {lastManualOccurrenceYearMonth} を開く
            </Link>
            <span style={{ opacity: 0.8 }}>
              公開するにはスケジュール月で validate / publish してください。
            </span>
          </div>
        ) : null}

        {isSessionReady && hasAdminRole && lesson && editState ? (
          <>
            <section style={{ display: "grid", gap: 8, padding: 12, border: "1px solid #e5e5e5", borderRadius: 12 }}>
              <strong>{lesson.title}</strong>
              <div style={{ fontSize: 14, opacity: 0.8 }}>
                {lesson.lessonKind} / {lesson.status} / {lesson.startDate} - {lesson.endDate}
              </div>
              <div style={{ fontSize: 14, opacity: 0.8 }}>
                {lesson.defaultStudioName ?? "会場未定"} / {lesson.primaryTeacherNameSnapshots.join(", ") || "講師未定"}
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {lesson.affectedYearMonths.map((yearMonth) => (
                  <Link
                    key={yearMonth}
                    to={`/t/${tenantSlug}/teacher/admin/schedule-months/${yearMonth}`}
                    style={{ fontSize: 12 }}
                  >
                    schedule {yearMonth}
                  </Link>
                ))}
              </div>
            </section>

            <section style={{ display: "grid", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>status transition</h2>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {transitions.length === 0 ? <span>遷移候補はありません。</span> : null}
                {transitions.map((nextStatus) => (
                  <button
                    key={nextStatus}
                    type="button"
                    onClick={() => void handleTransition(nextStatus)}
                    disabled={busyKey === `transition:${nextStatus}`}
                  >
                    {busyKey === `transition:${nextStatus}` ? "更新中..." : `${nextStatus} に変更`}
                  </button>
                ))}
              </div>
            </section>

            <section style={{ display: "grid", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>top-level 編集</h2>
              <form
                onSubmit={(event) => void handleSave(event)}
                style={{ display: "grid", gap: 12, padding: 12, border: "1px solid #e5e5e5", borderRadius: 12 }}
              >
                <label style={{ display: "grid", gap: 4 }}>
                  <span>title</span>
                  <input
                    value={editState.title}
                    onChange={(event) => setEditState((current) => current ? { ...current, title: event.target.value } : current)}
                  />
                </label>
                <label style={{ display: "grid", gap: 4 }}>
                  <span>shortTitle</span>
                  <input
                    value={editState.shortTitle}
                    onChange={(event) =>
                      setEditState((current) => current ? { ...current, shortTitle: event.target.value } : current)
                    }
                  />
                </label>
                <label style={{ display: "grid", gap: 4 }}>
                  <span>description</span>
                  <textarea
                    value={editState.description}
                    rows={3}
                    onChange={(event) =>
                      setEditState((current) => current ? { ...current, description: event.target.value } : current)
                    }
                  />
                </label>
                <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))" }}>
                  <label style={{ display: "grid", gap: 4 }}>
                    <span>genre</span>
                    <input
                      value={editState.genre}
                      onChange={(event) =>
                        setEditState((current) => current ? { ...current, genre: event.target.value } : current)
                      }
                    />
                  </label>
                  <label style={{ display: "grid", gap: 4 }}>
                    <span>targetLabel</span>
                    <input
                      value={editState.targetLabel}
                      onChange={(event) =>
                        setEditState((current) => current ? { ...current, targetLabel: event.target.value } : current)
                      }
                    />
                  </label>
                </div>
                <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))" }}>
                  <label style={{ display: "grid", gap: 4 }}>
                    <span>trialPolicyId</span>
                    <input
                      value={editState.trialPolicyId}
                      onChange={(event) =>
                        setEditState((current) => current ? { ...current, trialPolicyId: event.target.value } : current)
                      }
                    />
                  </label>
                  <label style={{ display: "grid", gap: 4 }}>
                    <span>visibilityPolicyId</span>
                    <input
                      value={editState.visibilityPolicyId}
                      onChange={(event) =>
                        setEditState((current) =>
                          current ? { ...current, visibilityPolicyId: event.target.value } : current
                        )
                      }
                    />
                  </label>
                  <label style={{ display: "grid", gap: 4 }}>
                    <span>sortOrder</span>
                    <input
                      value={editState.sortOrder}
                      onChange={(event) =>
                        setEditState((current) => current ? { ...current, sortOrder: event.target.value } : current)
                      }
                    />
                  </label>
                </div>
                <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input
                    type="checkbox"
                    checked={editState.publicVisible}
                    onChange={(event) =>
                      setEditState((current) => current ? { ...current, publicVisible: event.target.checked } : current)
                    }
                  />
                  <span>publicVisible</span>
                </label>
                <button type="submit" disabled={busyKey === "save"}>
                  {busyKey === "save" ? "保存中..." : "lesson を更新する"}
                </button>
              </form>
            </section>

            <section style={{ display: "grid", gap: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                <h2 style={{ margin: 0, fontSize: 18 }}>定期枠</h2>
                {lesson.lessonKind === "regular" ? (
                  <Link to={`/t/${tenantSlug}/teacher/admin/lessons/${lesson.lessonId}/recurring-slots/new`}>
                    定期枠を追加
                  </Link>
                ) : null}
              </div>
              <p style={{ margin: 0, fontSize: 14, opacity: 0.8 }}>
                定期枠はこのレッスン詳細から管理できます。変更しても既存の manual / edited / overrideApplied occurrence は直接上書きされず、対象月で generate すると auto occurrence に反映されます。
              </p>
              <p style={{ margin: 0, fontSize: 14, opacity: 0.8 }}>
                定期枠を無効化すると、今後 Generate したときにその枠の予定は作成されません。
              </p>
              {lesson.recurringSlots.length === 0 ? <p style={{ margin: 0 }}>定期枠はありません。</p> : null}
              <div style={{ display: "grid", gap: 12 }}>
                {lesson.recurringSlots.map((slot) => (
                  <article
                    key={slot.slotId}
                    style={{ padding: 12, border: "1px solid #e5e5e5", borderRadius: 12, display: "grid", gap: 6 }}
                  >
                    <strong>{slot.startTime} - {slot.endTime}</strong>
                    <div style={{ fontSize: 14, opacity: 0.8 }}>
                      {getRecurrencePresetLabel(slot.recurrencePreset)} / {WEEKDAY_LABELS[slot.weekday] ?? slot.weekday}曜日 / {slot.status}
                    </div>
                    <div style={{ fontSize: 14, opacity: 0.8 }}>
                      {slot.studioNameSnapshot ?? "会場未定"} / {slot.teacherNameSnapshots.join(", ") || "講師未定"}
                    </div>
                    <div style={{ fontSize: 12, opacity: 0.7 }}>
                      activeFrom {slot.activeFrom} / activeTo {slot.activeTo}
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <Link to={`/t/${tenantSlug}/teacher/admin/lessons/${lesson.lessonId}/recurring-slots/${slot.slotId}`}>
                        詳細/編集
                      </Link>
                    </div>
                  </article>
                ))}
              </div>
            </section>

            <section style={{ display: "grid", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>manual occurrences</h2>
              {lesson.manualOccurrences.length === 0 ? <p style={{ margin: 0 }}>manual occurrence はありません。</p> : null}
              <div style={{ display: "grid", gap: 12 }}>
                {lesson.manualOccurrences.map((occurrence) => (
                  <article
                    key={occurrence.occurrenceId}
                    style={{ padding: 12, border: "1px solid #e5e5e5", borderRadius: 12, display: "grid", gap: 6 }}
                  >
                    <Link to={`/t/${tenantSlug}/teacher/admin/occurrences/${occurrence.occurrenceId}`}>
                      {formatTenantIsoDate(occurrence.startAt)}
                    </Link>
                    <div style={{ fontSize: 14, opacity: 0.8 }}>
                      {formatTenantIsoTime(occurrence.startAt)} - {formatTenantIsoTime(occurrence.endAt)} / {occurrence.lifecycleStatus}
                    </div>
                    <div style={{ fontSize: 14, opacity: 0.8 }}>
                      {occurrence.studioNameSnapshot ?? "会場未定"} / {occurrence.teacherNameSnapshots.join(", ") || "講師未定"}
                    </div>
                    {occurrence.lifecycleStatus !== "cancelled" ? (
                      <button
                        type="button"
                        onClick={() => void handleCancelManualOccurrence(occurrence.occurrenceId)}
                        disabled={busyKey === `cancel:${occurrence.occurrenceId}`}
                      >
                        {busyKey === `cancel:${occurrence.occurrenceId}` ? "取消中..." : "manual occurrence を取消"}
                      </button>
                    ) : null}
                  </article>
                ))}
              </div>
            </section>

            <section style={{ display: "grid", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>個別予定を追加</h2>
              {lesson.lessonKind === "regular" && lesson.recurringSlots.length === 0 ? (
                <p style={{ margin: 0, fontSize: 14, opacity: 0.85 }}>
                  不定期レッスンはここから個別予定を追加してください。
                </p>
              ) : null}
              {lesson.lessonKind === "regular" ? (
                <p style={{ margin: 0, fontSize: 14, opacity: 0.85 }}>
                  発表会前の追加レッスンや、通常枠の外で行う 1 回分の予定をここから登録できます。
                </p>
              ) : null}
              <form
                onSubmit={(event) => void handleCreateManualOccurrence(event)}
                style={{ display: "grid", gap: 12, padding: 12, border: "1px solid #e5e5e5", borderRadius: 12 }}
              >
                <label style={{ display: "grid", gap: 4 }}>
                  <span>occurrenceDate</span>
                  <input
                    type="date"
                    required
                    value={manualOccurrenceForm.occurrenceDate}
                    onChange={(event) =>
                      setManualOccurrenceForm((current) => ({ ...current, occurrenceDate: event.target.value }))
                    }
                  />
                </label>
                <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))" }}>
                  <label style={{ display: "grid", gap: 4 }}>
                    <span>startTime</span>
                    <input
                      type="time"
                      required
                      value={manualOccurrenceForm.startTime}
                      onChange={(event) =>
                        setManualOccurrenceForm((current) => ({ ...current, startTime: event.target.value }))
                      }
                    />
                  </label>
                  <label style={{ display: "grid", gap: 4 }}>
                    <span>endTime</span>
                    <input
                      type="time"
                      required
                      value={manualOccurrenceForm.endTime}
                      onChange={(event) =>
                        setManualOccurrenceForm((current) => ({ ...current, endTime: event.target.value }))
                      }
                    />
                  </label>
                </div>
                <label style={{ display: "grid", gap: 4 }}>
                  <span>studio</span>
                  <select
                    value={manualOccurrenceForm.studioId}
                    onChange={(event) =>
                      setManualOccurrenceForm((current) => ({ ...current, studioId: event.target.value }))
                    }
                    disabled={manualOccurrenceForm.studioDecisionStatus === "tbd"}
                  >
                    <option value="">選択してください</option>
                    {studios?.items.map((studio) => (
                      <option key={studio.studioId} value={studio.studioId}>
                        {studio.displayName}
                      </option>
                    ))}
                  </select>
                </label>
                <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input
                    type="checkbox"
                    checked={manualOccurrenceForm.studioDecisionStatus === "tbd"}
                    onChange={(event) =>
                      setManualOccurrenceForm((current) => ({
                        ...current,
                        studioDecisionStatus: event.target.checked ? "tbd" : "confirmed",
                        studioId: event.target.checked ? "" : current.studioId
                      }))
                    }
                  />
                  <span>studio tbd</span>
                </label>
                <fieldset style={{ display: "grid", gap: 8, border: "none", padding: 0, margin: 0 }}>
                  <legend style={{ padding: 0 }}>teachers</legend>
                  <div style={{ display: "grid", gap: 8 }}>
                    {teacherMembers.map((member) => (
                      <label key={member.platformUserId} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <input
                          type="checkbox"
                          checked={manualOccurrenceForm.teacherPlatformUserIds.includes(member.platformUserId)}
                          disabled={manualOccurrenceForm.teacherAssignmentStatus === "tbd"}
                          onChange={(event) =>
                            setManualOccurrenceForm((current) => ({
                              ...current,
                              teacherPlatformUserIds: toggleSelection(
                                current.teacherPlatformUserIds,
                                member.platformUserId,
                                event.target.checked
                              )
                            }))
                          }
                        />
                        <span>{getMemberDisplayName(member)}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
                <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input
                    type="checkbox"
                    checked={manualOccurrenceForm.teacherAssignmentStatus === "tbd"}
                    onChange={(event) =>
                      setManualOccurrenceForm((current) => ({
                        ...current,
                        teacherAssignmentStatus: event.target.checked ? "tbd" : "assigned",
                        teacherPlatformUserIds: event.target.checked ? [] : current.teacherPlatformUserIds
                      }))
                    }
                  />
                  <span>teacher tbd</span>
                </label>
                <label style={{ display: "grid", gap: 4 }}>
                  <span>publicNote</span>
                  <input
                    value={manualOccurrenceForm.publicNote}
                    onChange={(event) =>
                      setManualOccurrenceForm((current) => ({ ...current, publicNote: event.target.value }))
                    }
                  />
                </label>
                <label style={{ display: "grid", gap: 4 }}>
                  <span>operatorNote</span>
                  <input
                    value={manualOccurrenceForm.operatorNote}
                    onChange={(event) =>
                      setManualOccurrenceForm((current) => ({ ...current, operatorNote: event.target.value }))
                    }
                  />
                </label>
                <label style={{ display: "grid", gap: 4 }}>
                  <span>adminNote</span>
                  <input
                    value={manualOccurrenceForm.adminNote}
                    onChange={(event) =>
                      setManualOccurrenceForm((current) => ({ ...current, adminNote: event.target.value }))
                    }
                  />
                </label>
                <button type="submit" disabled={busyKey === "create-manual-occurrence"}>
                  {busyKey === "create-manual-occurrence" ? "追加中..." : "個別予定を追加する"}
                </button>
                <div style={{ fontSize: 12, opacity: 0.8 }}>
                  追加後は lesson 詳細を再読込し、スケジュール月で validate / publish すると family / teacher / public に反映されます。
                </div>
              </form>
            </section>
          </>
        ) : null}
      </div>
    </OperatorPageScaffold>
  );
}
